import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ExchangeSelector, { exchanges } from './ExchangeSelector';
import TradingPairSelector, { toTradingPair } from './TradingPairSelector';
import FundingRateResult from './FundingRateResult';
import { PERIOD_ORDER, defaultPeriodLocale, computePeriod, formatShortRange, CustomRangeFields } from './PeriodSelector';
import { useStuck, DockSentinel, DockShell, DockRow, ExpandButton, ChipStrip, Chip, StripHint } from './WorkspaceDock';
import { riseIn } from './motion';
import apiService from '../services/api';
import {
  Card,
  Segmented,
  SegmentedItem,
  StatusDot,
  Rate,
  rateTone,
  pressable,
  IconCalendar,
  IconBank,
  IconCoins,
  IconActivity,
  IconSwap,
} from './ui';

// Exchanges where the contract type can be chosen, and those whose funding API takes it.
const CONTRACT_TYPE_EXCHANGES = ['binance', 'htx', 'okx', 'kucoin', 'gate', 'mexc', 'bybit', 'bitget', 'aster', 'hyperliquid', 'bitfinex', 'cryptocom', 'coinex', 'poloniex', 'xtcom', 'bitmart', 'bingx', 'bithumb', 'upbit'];
const FUNDING_CONTRACT_EXCHANGES = CONTRACT_TYPE_EXCHANGES.filter((id) => id !== 'bithumb' && id !== 'upbit');

const defaultDockLocale = {
  period: 'Period',
  exchange: 'Exchange',
  pair: 'Pair',
  all: 'All',
  hide: 'Hide',
  chooseExchangeFirst: 'Choose an exchange first',
  loadingPairs: 'Loading pairs…',
  noPairs: 'No pairs',
  emptyTitle: 'Choose a trading pair',
  emptyText: 'Statistics, the funding rate chart and the full history will appear here.',
};

// Server may return raw records instead of totals — compute the summary on the client then.
const normalizeFundingResponse = (resp) => {
  if (!resp) return null;
  if (resp.average_funding_rate !== undefined || (resp.period_1d && resp.period_1d.average_funding_rate !== undefined)) {
    return resp;
  }
  const arr = resp.data && Array.isArray(resp.data) ? resp.data : null;
  if (!arr) return resp;
  const values = [];
  const mapped = arr.map((item) => {
    const fr = item.fundingRate ?? item.funding_rate ?? item.funding_rate_raw ?? item.fundingRateRaw ?? null;
    const num = fr !== null ? Number(fr) : NaN;
    if (Number.isFinite(num)) values.push(num);
    return { ...item, fundingRate: Number.isFinite(num) ? num : null };
  });
  const total_records = values.length;
  const total_funding_rate = values.reduce((s, v) => s + v, 0);
  return {
    average_funding_rate: total_records ? total_funding_rate / total_records : 0,
    total_funding_rate,
    total_records,
    min_rate: total_records ? Math.min(...values) : 0,
    max_rate: total_records ? Math.max(...values) : 0,
    data: mapped,
    raw: resp,
  };
};

const Wrapper = styled.div`
  position: relative;
`;

const ResultCard = styled(Card)`
  margin-top: 16px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: clamp(32px, 6vw, 64px) 16px;
  text-align: center;
  ${riseIn(0, 600)}

  h3 {
    font-size: 1.1875rem;
    font-weight: 600;
    letter-spacing: -0.018em;
    color: var(--text);
  }

  p {
    max-width: 30em;
    font-size: 0.9375rem;
    color: var(--text-secondary);
  }
`;

const EmptyIcon = styled.span`
  display: inline-grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin-bottom: 6px;
  border-radius: 15px;
  background: var(--accent-tint);
  color: var(--accent);
`;

const ContractSegmented = styled(Segmented)`
  padding: 2px;

  ${SegmentedItem} {
    padding: 5px 10px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  @media (max-width: 700px) {
    display: none;
  }
`;

const ContractToggle = styled.button`
  display: none;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 980px;
  background: var(--fill);
  color: var(--text);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  ${pressable}

  svg {
    color: var(--text-tertiary);
  }

  @media (max-width: 700px) {
    display: inline-flex;
  }
`;

const formatChipRate = (rate) => {
  const percent = Number(rate) * 100;
  if (!Number.isFinite(percent)) return '';
  return `${percent > 0 ? '+' : ''}${percent.toFixed(4)}%`;
};

function Home({ locale }) {
  const {
    periodSelector = defaultPeriodLocale,
    exchangeSelector,
    tradingPairSelector,
    fundingRateResult,
  } = locale;
  const dock = { ...defaultDockLocale, ...locale.dock };

  const [periodType, setPeriodType] = useState('month');
  const [selectedPeriod, setSelectedPeriod] = useState(() => computePeriod('month'));
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [selectedTradingPair, setSelectedTradingPair] = useState(null);
  const [fundingRateData, setFundingRateData] = useState(null);
  const [loadingFundingRate, setLoadingFundingRate] = useState(false);
  const [contractType, setContractType] = useState('usdt-m'); // За замовчуванням USDT-M
  const [pairsState, setPairsState] = useState({ pairs: [], loading: false });
  // Which picker is unfolded in the dock. First visit starts with the exchange grid open.
  const [openPanel, setOpenPanel] = useState('exchange');
  const [sentinelRef, stuck] = useStuck();

  const showContractType = CONTRACT_TYPE_EXCHANGES.includes(selectedExchange?.id);
  const togglePanel = (name) => setOpenPanel((current) => (current === name ? null : name));

  // ---------------------------------------------------------------- selection handlers

  const handlePeriodSelect = (type) => {
    if (type === 'custom') {
      if (periodType === 'custom') {
        togglePanel('period');
        return;
      }
      // Start the custom range from whatever is currently shown.
      setPeriodType('custom');
      setSelectedPeriod(computePeriod('custom', selectedPeriod));
      setOpenPanel('period');
      return;
    }
    setPeriodType(type);
    setSelectedPeriod(computePeriod(type));
    setOpenPanel((current) => (current === 'period' ? null : current));
  };

  const handleCustomRange = (range) => {
    setSelectedPeriod(computePeriod('custom', range));
  };

  const handleExchangeChange = (exchange) => {
    if (exchange?.id === selectedExchange?.id) {
      setOpenPanel((current) => (current === 'exchange' ? null : current));
      return;
    }
    setSelectedExchange(exchange);
    setSelectedTradingPair(null);
    setFundingRateData(null);
    // Якщо біржа не підтримує вибір типу контракту, скидаємо на дефолтний
    if (exchange?.id && !CONTRACT_TYPE_EXCHANGES.includes(exchange.id)) {
      setContractType('usdt-m');
    }
    setOpenPanel('pair');
  };

  const handleContractTypeChange = (type) => {
    if (type === contractType) return;
    setContractType(type);
    // Скидаємо дані при зміні типу контракту
    setSelectedTradingPair(null);
    setFundingRateData(null);
    setOpenPanel('pair');
  };

  const handleTradingPairChange = (pair) => {
    setSelectedTradingPair(pair);
    setOpenPanel((current) => (current === 'pair' ? null : current));
  };

  // Load funding history whenever the period, exchange, pair or contract type changes.
  // A newer selection cancels the older request, so fast switching never shows stale data.
  useEffect(() => {
    if (!selectedPeriod || !selectedExchange?.id || !selectedTradingPair) return undefined;
    let cancelled = false;
    setLoadingFundingRate(true);
    apiService
      .getFundingRate(
        selectedExchange.id,
        selectedTradingPair.symbol,
        selectedPeriod.startDate,
        selectedPeriod.endDate,
        FUNDING_CONTRACT_EXCHANGES.includes(selectedExchange.id) ? contractType : null
      )
      .then((response) => {
        if (!cancelled) setFundingRateData(normalizeFundingResponse(response));
      })
      .catch((error) => {
        console.error('❌ Помилка завантаження ставки фінансування:', error);
        if (!cancelled) setFundingRateData(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingFundingRate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPeriod, selectedExchange, selectedTradingPair, contractType]);

  useEffect(() => {
    if (!openPanel) return undefined;
    const onKey = (event) => event.key === 'Escape' && setOpenPanel(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPanel]);

  // ---------------------------------------------------------------- strips

  const periodLabel = (id) => {
    if (id === 'custom' && periodType === 'custom') return formatShortRange(selectedPeriod, periodSelector.dateLocale);
    return periodSelector.periodTypes.find((type) => type.id === id)?.label || id;
  };

  const periodStrip = (
    <ChipStrip label={dock.period} selectedKey={`${periodType}:${periodLabel('custom')}`}>
      {PERIOD_ORDER.map((id) => (
        <Chip key={id} type="button" aria-pressed={periodType === id} onClick={() => handlePeriodSelect(id)}>
          {id === 'custom' && <IconCalendar size={15} />}
          {periodLabel(id)}
        </Chip>
      ))}
    </ChipStrip>
  );

  const exchangeStrip = (
    <ChipStrip label={dock.exchange} selectedKey={selectedExchange?.id} itemsKey={exchanges.length}>
      {exchanges.map((exchange) => (
        <Chip
          key={exchange.id}
          type="button"
          aria-pressed={selectedExchange?.id === exchange.id}
          onClick={() => handleExchangeChange(exchange)}
        >
          <StatusDot $active={exchange.status === 'active'} />
          {exchange.name}
        </Chip>
      ))}
    </ChipStrip>
  );

  const selectedSymbol = selectedTradingPair?.symbol;
  const stripPairs =
    selectedTradingPair && !pairsState.pairs.some((pair) => pair.symbol === selectedSymbol)
      ? [{ symbol: selectedSymbol, lastFundingRate: (selectedTradingPair.change24h || 0) / 100 }, ...pairsState.pairs]
      : pairsState.pairs;

  let pairStrip;
  if (!selectedExchange) {
    pairStrip = <StripHint>{dock.chooseExchangeFirst}</StripHint>;
  } else if (pairsState.loading && stripPairs.length === 0) {
    pairStrip = <StripHint>{dock.loadingPairs}</StripHint>;
  } else if (stripPairs.length === 0) {
    pairStrip = <StripHint>{dock.noPairs}</StripHint>;
  } else {
    pairStrip = (
      <ChipStrip label={dock.pair} selectedKey={selectedSymbol} itemsKey={stripPairs.map((pair) => pair.symbol).join(',')}>
        {stripPairs.map((pair) => (
          <Chip
            key={pair.symbol}
            type="button"
            aria-pressed={selectedSymbol === pair.symbol}
            onClick={() => handleTradingPairChange(toTradingPair(pair))}
          >
            {pair.symbol}
            <small>
              <Rate $tone={rateTone(pair.lastFundingRate)}>{formatChipRate(pair.lastFundingRate)}</Rate>
            </small>
          </Chip>
        ))}
      </ChipStrip>
    );
  }

  const contractControls = showContractType && (
    <>
      <ContractSegmented role="group" aria-label={locale.contractTypeTitle}>
        {['usdt-m', 'coin-m'].map((type) => (
          <SegmentedItem
            key={type}
            type="button"
            $active={contractType === type}
            aria-pressed={contractType === type}
            onClick={() => handleContractTypeChange(type)}
          >
            {type.toUpperCase()}
          </SegmentedItem>
        ))}
      </ContractSegmented>
      <ContractToggle
        type="button"
        aria-label={`${locale.contractTypeTitle}: ${contractType.toUpperCase()}`}
        onClick={() => handleContractTypeChange(contractType === 'usdt-m' ? 'coin-m' : 'usdt-m')}
      >
        {contractType.toUpperCase()}
        <IconSwap size={13} strokeWidth={2.2} />
      </ContractToggle>
    </>
  );

  return (
    <Wrapper>
      <DockSentinel ref={sentinelRef} />
      <DockShell $stuck={stuck}>
        <DockRow
          icon={IconCalendar}
          label={dock.period}
          strip={periodStrip}
          panelId="dock-period"
          open={openPanel === 'period'}
          panel={
            <CustomRangeFields
              startDate={selectedPeriod.startDate}
              endDate={selectedPeriod.endDate}
              onChange={handleCustomRange}
              locale={periodSelector}
            />
          }
        />
        <DockRow
          icon={IconBank}
          label={dock.exchange}
          strip={exchangeStrip}
          actions={
            <>
              {contractControls}
              <ExpandButton
                open={openPanel === 'exchange'}
                onClick={() => togglePanel('exchange')}
                label={openPanel === 'exchange' ? dock.hide : dock.all}
                controls="dock-exchange"
              />
            </>
          }
          panelId="dock-exchange"
          open={openPanel === 'exchange'}
          panel={
            <ExchangeSelector selectedExchange={selectedExchange} onExchangeChange={handleExchangeChange} locale={exchangeSelector} />
          }
        />
        <DockRow
          icon={IconCoins}
          label={dock.pair}
          strip={pairStrip}
          actions={
            <ExpandButton
              open={openPanel === 'pair'}
              onClick={() => togglePanel('pair')}
              label={openPanel === 'pair' ? dock.hide : dock.all}
              controls="dock-pair"
            />
          }
          panelId="dock-pair"
          open={openPanel === 'pair'}
          panel={
            <TradingPairSelector
              compact
              selectedExchange={selectedExchange}
              selectedTradingPair={selectedTradingPair}
              onTradingPairChange={handleTradingPairChange}
              onPairsChange={setPairsState}
              locale={tradingPairSelector}
              contractType={showContractType ? contractType : null}
            />
          }
        />
      </DockShell>

      <ResultCard>
        {selectedTradingPair ? (
          <FundingRateResult
            tradingPair={selectedTradingPair}
            period={selectedPeriod}
            fundingRateData={fundingRateData}
            loading={loadingFundingRate}
            locale={fundingRateResult}
            exchangeId={selectedExchange?.id}
          />
        ) : (
          <Empty>
            <EmptyIcon>
              <IconActivity size={24} />
            </EmptyIcon>
            <h3>{dock.emptyTitle}</h3>
            <p>{dock.emptyText}</p>
          </Empty>
        )}
      </ResultCard>
    </Wrapper>
  );
}

export default Home;
